import moment from 'moment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  AuctionResultStatus,
  BidStatus,
  IAuction,
  IBidResultDetail,
  MillisecondBeforeClosing,
  IItaAuctionResult,
  IProductResult,
  ISourceAuction,
} from '.';
import { getAuctionTimezone } from '../core';
import { AuctionClient, BidClient } from '../core/clients';
import { axiosToObservable } from '../core/clients/axiosToObservable';

export enum ExchangeCode {
  Jao = 'JAO',
  Empire = 'EMPIRE'
}

export enum ExchangeId {
  Jao = 1,
  Empire = 2
}

export class AuctionService {
  getAuctions = (
    corridorCodes?: string[],
    auctionDateTime?: Date
  ): Observable<IAuction[]> => {
    return axiosToObservable(AuctionClient.post<IAuction[]>('auction/by/corridorCodes', {
      corridorCodes,
      auctionDateTime,
    })).pipe(
      map((auctions: IAuction[]) => {
        return this.getAuctionData(auctions) || ([] as IAuction[]);
      })
    );
  };

  getAuctionCalendar = (
    corridorCodes?: string[]
  ): Observable<IAuction[]> => {
    return axiosToObservable(AuctionClient.post<IAuction[]>('auction/calendar/by/corridorCodes', {
      corridorCodes
    })).pipe(
      map((auctionsCalendar: IAuction[]) => {
        return this.getAuctionData(auctionsCalendar) || ([] as IAuction[]);
      })
    );
  };

  getAuctionResults = (identification: string): Observable<IProductResult[]> => {
    return axiosToObservable(AuctionClient.get<IProductResult[]>('auction/all-results/' + identification)).pipe(
      map((results: IProductResult[]) => {
        return results || ([] as IProductResult[]);
      })
    );
  };
  
  compareTwoCorridorList = (first: string[], second: string[]): boolean => {
    let found = false;
    first.every((f) => {
      return second.every((s) => {
        if (f === s) {
          found = true;
          return false;
        }
        return true;
      });
    });
    return found;
  };

  assignBidResultsToAuctions = (
    auctions: IAuction[],
    bidResults: IBidResultDetail[],
    filterDate?: Date
  ): IAuction[] => {
    return auctions.map((x) => {
      const auction = this.assignBidResultToAuction(x, bidResults, filterDate);
      return auction;
    });
  };

  assignBidResultToAuction = (
    auction: IAuction,
    bidResultDetailList: IBidResultDetail[],
    filterDate?: Date
  ): IAuction => {
    let status = BidStatus.Open;
    const dateTimeBeforeClosure = moment(auction.biddingClose).add(
      -MillisecondBeforeClosing,
      'ms'
    );

    if (
      moment().isBetween(dateTimeBeforeClosure, moment(auction.biddingClose))
    ) {
      status = BidStatus.Closing;
    }

    if (bidResultDetailList && bidResultDetailList.length > 0) {
      const bidResult = bidResultDetailList.find(
        (bid) => bid.bidResult.documentId === auction.identification
      );
      if (bidResult) {
        auction.bidResult = bidResult.bidResult;
        auction.hasSuccessfullyBid = bidResult.hasSuccessfullyBid;
        auction.successfullyBidResult = bidResult.successfullyBidResult;
        status = bidResult.bidResult.status as BidStatus;
      }
    }

    const convertToTimeZone = moment()
      .tz(getAuctionTimezone())
      .startOf('day')
      .toISOString();
    const startOfDay = moment(convertToTimeZone);
    const isoFilterDate = moment(filterDate).toISOString();
    const isoBiddingClose = moment(auction.biddingClose).toISOString();

    if (
      moment().isAfter(moment(isoBiddingClose)) ||
      moment(isoFilterDate).isBefore(startOfDay)
    ) {
      status = BidStatus.Closed;
    }

    if (auction) {
      auction.bidStatus = status as BidStatus;
    }
    return auction;
  };

  assignItaAuctionResultsToAuctions = (
    auctions: IAuction[],
    auctionsResult: IItaAuctionResult[]
  ): IAuction [] => {
    return auctions.map((x) => {
      const auction = this.assignItaAuctionResultToAuction(x, auctionsResult);
      return auction;
    });
  };

  assignItaAuctionResultToAuction = (
    auction: IAuction,
    auctionsResults: IItaAuctionResult[]
  ): IAuction => {
    auction.auctionResultStatus = AuctionResultStatus.AuctionOpen   
    if (auction.bidStatus === BidStatus.Closed && !auction.hasSuccessfullyBid) {
      auction.auctionResultStatus = AuctionResultStatus.NoSubmission
    }   
    else if (auction.bidStatus === BidStatus.Closed && auction.hasSuccessfullyBid) {
      auction.auctionResultStatus = AuctionResultStatus.AwaitingResults
    }
    if (auctionsResults != null && auctionsResults.length > 0) {
      const auctionResult = auctionsResults.find(ars => ars.auctionId === auction.auctionName)
      if (auctionResult != null) {
        if (auctionResult.hasCapacityWon)
        {
          auction.auctionResultStatus = AuctionResultStatus.CapacityWon
          auction.auctionResult = auctionResult
        }
        else
          auction.auctionResultStatus = AuctionResultStatus.NoCapacityWon
      }     
    }
    return auction;
  };

  private getCorridorCode = (code: string) => {
    const codeSplit = code.split('-');
    if (codeSplit.length > 2) {
      return `${codeSplit[1]}-${codeSplit[2]}`;
    }

    return code;
  };

  private getInterconnectorCode = (code: string) => {
    const codeSplit = code.split('-');
    if (codeSplit.length > 2) {
      return `${codeSplit[0]}`;
    }
    return 'N/A';
  };

  private getAuctionData = (auctionData?: IAuction[]) => {
    return auctionData
      ? auctionData?.map((x: IAuction) => {
          return {
            ...x,
            corridor: this.getCorridorCode(x.corridor),
            interconnector: this.getInterconnectorCode(x.corridor),
          };
        })
      : ([] as IAuction[]);
  };
}
